import { ICustomFieldResponse } from '@sparelabs/api-client'
import { FormContentType, IFormContentResponse } from '@sparelabs/engage-client'
import { Banner, infoBannerTheme, Markdown, RoundedButton, SubmitButtonTheme } from '@sparelabs/nutshell-legacy'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionButtonsWrapper,
  BannerContainer,
  CustomFieldWithLabel,
  FieldContainer,
  FormBackButton,
  FormContainer,
} from 'src/components'
import { FormContinueButton } from 'src/components/buttons/FromContinueButton'
import { isCustomField, isFormContent } from 'src/helpers'
import { st } from 'src/locales/TranslationHelper'
import { useFormContext } from '../../FormContext'
import { FormSubmitted } from '../../FormStepTypes'

export type IFormField = ICustomFieldResponse | IFormContentResponse

interface IFormSection {
  fields: IFormField[]
}

interface IProps {
  fields: IFormField[]
  onSubmit: (metadata: Record<string, unknown>) => void
  showValidation: boolean
}

const isDivider = (field: IFormField): boolean => isFormContent(field) && field.type === FormContentType.Divider

// Only show the banner when the form has never been submitted and this isn't a "new case"/intake form
const showNotSubmittedBanner = (formSubmitted: FormSubmitted, caseId?: string): boolean =>
  formSubmitted === FormSubmitted.Never && Boolean(caseId)

export const FormInputForm = ({ fields, onSubmit, showValidation }: IProps): JSX.Element => {
  const { caseId, formSubmitted, prevCaseForm, onPrevStep, updatePercentComplete } = useFormContext()
  const [metadata, setMetadata] = useState<Record<string, unknown>>(prevCaseForm?.metadata || {})

  const [sectionIndex, setSectionIndex] = useState(0)

  const sections = useMemo(() => {
    const result: IFormSection[] = []

    let currentSection: IFormSection = { fields: [] }
    for (const field of fields) {
      if (isDivider(field) && currentSection.fields.length) {
        // Finding a divider means were're starting a new section
        result.push(currentSection)
        currentSection = { fields: [field] }
      } else {
        currentSection.fields.push(field)
      }
    }
    result.push(currentSection)

    return result.filter(
      ({ fields }) => fields.length, // Don't return sections without any fields
    )
  }, [fields])

  useEffect(() => {
    // Set initial percent on first load
    updatePercentComplete((1 / sections.length) * 100)
  }, [sections, updatePercentComplete])

  const onNextSection = useCallback(() => {
    setSectionIndex((prev) => (prev < sections.length - 1 ? prev + 1 : prev))

    const newSectionIndex = sectionIndex + 1
    updatePercentComplete(((newSectionIndex + 1) / sections.length) * 100)
  }, [sectionIndex, sections.length, updatePercentComplete])

  const onPrevSection = useCallback(() => {
    const isOnFirstSection = sectionIndex === 0
    if (isOnFirstSection) {
      onPrevStep() // Can't go back in the section, go back a step
      updatePercentComplete(0)
    } else {
      setSectionIndex((prev) => (prev > 0 ? prev - 1 : prev))

      const newSectionIndex = sectionIndex - 1
      updatePercentComplete(((newSectionIndex + 1) / sections.length) * 100)
    }
  }, [onPrevStep, sectionIndex, sections.length, updatePercentComplete])

  const isLastSection = sectionIndex === sections.length - 1

  return (
    <FormContainer>
      <FieldContainer>
        {showNotSubmittedBanner(formSubmitted, caseId) && (
          <BannerContainer>
            <Banner bannerTheme={infoBannerTheme}>{st.forms.pageLayout.formNotYetSubmitted()}</Banner>
          </BannerContainer>
        )}
        {(sections[sectionIndex]?.fields ?? []).map((formItem, index) => {
          if (isCustomField(formItem)) {
            return (
              <CustomFieldWithLabel
                key={formItem.key}
                field={formItem}
                showValidation={showValidation}
                metadata={metadata}
                onChange={setMetadata}
              />
            )
          }
          if (isFormContent(formItem)) {
            return (
              <Markdown key={index} skipHtml={false}>
                {formItem.content}
              </Markdown>
            )
          }
          return null
        })}
      </FieldContainer>
      <ActionButtonsWrapper>
        <FormBackButton onClick={onPrevSection} />
        {!isLastSection && <FormContinueButton onClick={onNextSection} />}
        {isLastSection && (
          <RoundedButton buttonTheme={SubmitButtonTheme} onClick={() => onSubmit(metadata)}>
            {st.common.submit()}
          </RoundedButton>
        )}
      </ActionButtonsWrapper>
    </FormContainer>
  )
}
